import type { AvailableIconName } from '@/assets/icons';
import { STRINGS } from '@/constants/strings';
import {
  getPathwayMaterialBookUrl,
  toPathwayMaterialDisplay,
} from '@/features/player-session/constants/pathway-material-links';
import { BadgeName, CheckpointStatus } from '@/features/player-session/types/types';
import { sortProgressCheckpoints } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import { formatProgressCheckpointTitleOverview } from '@/features/player/utils/progress-title';

/** Levels 6–10 have no published magazine yet. */
const PATHWAY_MATERIAL_FALLBACK_URL = 'https://foundation.uskidsgolf.com/L1-3';

export enum StepViewMode {
  Default = 'default',
  Incomplete = 'incomplete',
  Achieved = 'achieved',
  LevelComplete = 'levelComplete',
}

export enum StepToken {
  Forms = 'forms',
  Skill1 = 'skill1',
  Skill2 = 'skill2',
}

export function buildCheckpointMapping(params: {
  level: number;
  badgeName: BadgeName;
  checkpointName: string;
}): string {
  const { level, badgeName, checkpointName } = params;
  return `level${level}/badges/${badgeName}/checkpoints/${checkpointName}`;
}

export function buildCheckpointMappingArray(params: {
  level: number;
  badgeName: BadgeName;
  checkpointName: string;
}): string[] {
  return [buildCheckpointMapping(params)];
}

export function normalizeToken(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function splitCheckpointKey(name: string): {
  group: string;
  suffix: string;
  hasDelimiter: boolean;
} {
  const lower = String(name ?? '')
    .trim()
    .toLowerCase();
  const hasDelimiter = lower.includes('.') || lower.includes(':');
  const parts = lower.includes('.') ? lower.split('.') : lower.split(':');
  const group = normalizeToken(parts[0]);
  const suffix = normalizeToken(parts.slice(1).join('.'));
  return { group, suffix, hasDelimiter };
}

export function getStepTokenFromName(rawName: string): StepToken | null {
  const key = splitCheckpointKey(rawName);
  const token = key.hasDelimiter ? key.suffix : normalizeToken(rawName);
  if (token.includes('forms') || token === 'form') return StepToken.Forms;
  if (token.includes('skill1')) return StepToken.Skill1;
  if (token.includes('skill2')) return StepToken.Skill2;
  return null;
}

export function isHideProgressStep(rawName: string): boolean {
  return getStepTokenFromName(rawName) !== null;
}

export function getStepIconName(rawName: string): AvailableIconName {
  const token = getStepTokenFromName(rawName);
  if (token === StepToken.Forms) return 'IcForm';
  if (token === StepToken.Skill1 || token === StepToken.Skill2) return 'IcScore';
  return 'IcScore';
}

export function getStepDisplayLabelFromSuffix(suffix: string): string {
  const s = normalizeToken(suffix);
  if (s.includes('forms') || s === 'form')
    return STRINGS.player.profile.progressTrackerContent.checkpointTitles.forms;
  if (s.includes('skill1')) return STRINGS.playerSession.stepDetail.skill1Label;
  if (s.includes('skill2')) return STRINGS.playerSession.stepDetail.skill2Label;
  return suffix;
}

export function getStepPanelTitle(mode: StepViewMode, stepLabel: string): string {
  if (mode === StepViewMode.Incomplete) {
    return STRINGS.playerSession.stepDetail.incompleteTitle(stepLabel);
  }
  return stepLabel;
}

export function getStepPanelBody(mode: StepViewMode, level: number, stepLabel: string): string {
  const copy = STRINGS.playerSession.stepDetail;
  if (mode === StepViewMode.Incomplete) return copy.incompleteBody;

  const isForms = normalizeToken(stepLabel) === 'forms';
  if (isForms) return copy.instructionForms;
  if (level >= 6) return copy.instructionRevision;
  return copy.instructionLink(
    toPathwayMaterialDisplay(getPathwayMaterialBookUrl(level) ?? PATHWAY_MATERIAL_FALLBACK_URL),
  );
}

export type BadgeRef = { name: BadgeName; label: string };
export type GroupRef = { key: string; label: string };

export type CheckpointItem = {
  key: string;
  label: string;
  icon: AvailableIconName;
  data: playerSession.Badge;
  hideProgress: boolean;
  onPress?: () => void;
};

/**
 * Applied when unset succeeds but the API omits `progress` (still update UI like iOS row refresh).
 * Deep-enough clone via `.map` — avoid mutating TanStack Query cached objects.
 */
export function patchProgressAfterCheckpointUnset(params: {
  progress: playerSession.Progress[] | undefined | null;
  levelIndex: number;
  badgeName: BadgeName;
  checkpointKey: string;
}): playerSession.Progress[] {
  const { progress, levelIndex, badgeName, checkpointKey } = params;
  if (!progress?.length || levelIndex < 0 || levelIndex >= progress.length) {
    return progress ? [...progress] : [];
  }

  return progress.map((p, idx) => {
    if (idx !== levelIndex) return p;
    const badges = (p.badges ?? []).map(b => {
      if (b.name !== badgeName) return b;
      const checkpoints = (b.checkpoints ?? []).map(cp =>
        String(cp.name) === checkpointKey
          ? { ...cp, status: CheckpointStatus.Unattempted, attempts: [] }
          : cp,
      );
      const badgeEarned = checkpoints.every(c => String(c.status) === CheckpointStatus.Completed);
      return { ...b, checkpoints, badgeEarned };
    });
    return { ...p, badges };
  });
}

function iconForCheckpoint(activeBadgeName: BadgeName, checkpointName: string): AvailableIconName {
  // Special-case checkpoint icons (as requested).
  const key = splitCheckpointKey(checkpointName);
  const token = key.hasDelimiter ? key.suffix : normalizeToken(checkpointName);
  if (token === 'terminology') return 'IcScore';

  // Step icons are fixed everywhere (Forms -> Form icon, Skills -> Score icon),
  // except Putting skill steps use the Putting icon.
  const stepToken = getStepTokenFromName(checkpointName);
  if (stepToken === StepToken.Skill1 || stepToken === StepToken.Skill2) {
    if (activeBadgeName === BadgeName.Putting) return 'IcPutting';
    return getStepIconName(checkpointName);
  }
  if (stepToken) return getStepIconName(checkpointName);

  // Category-wide icon rules (as requested).
  if (activeBadgeName === BadgeName.Aroundthegreen) return 'IcAroundthegreen';
  if (activeBadgeName === BadgeName.Fullswing) return 'IcFullSwing';
  if (activeBadgeName === BadgeName.Knowledge) return 'IcForm';
  if (activeBadgeName === BadgeName.Mastery) return 'IcScore';

  // Putting: form vs skill (non-step items still show score icon).
  if (activeBadgeName === BadgeName.Putting) return 'IcScore';

  return 'IcScore';
}

function sortNamesLikeProgress(names: string[]): string[] {
  const sorted = sortProgressCheckpoints(
    names.map(n => ({ name: n, status: '', attempts: [] })) as any,
  );
  return sorted.map(s => String((s as any).name ?? ''));
}

/**
 * Builds the horizontal list items for "checkpoint mode" in PlayerSessionCard.
 * Includes 2-layer drilldown for prefixed checkpoints (e.g. "sand.forms").
 */
export function buildCheckpointItems(params: {
  activeBadge: BadgeRef | null;
  activeBadgeData: playerSession.Badge | undefined;
  activeGroup: GroupRef | null;
  onSelectGroup: (group: GroupRef | null) => void;
}): CheckpointItem[] {
  const { activeBadge, activeBadgeData, activeGroup, onSelectGroup } = params;
  if (!activeBadge || !activeBadgeData) return [];

  const checkpoints = Array.isArray(activeBadgeData.checkpoints) ? activeBadgeData.checkpoints : [];
  const activeBadgeName = activeBadge.name;

  const alwaysHideProgress =
    activeBadgeName === BadgeName.Mastery || activeBadgeName === BadgeName.Knowledge;

  const hasAnyPrefixed = checkpoints.some(cp => {
    const n = String(cp.name ?? '');
    return n.includes('.') || n.includes(':');
  });

  if (hasAnyPrefixed) {
    const map = new Map<
      string,
      {
        key: string;
        label: string;
        icon: AvailableIconName;
        checkpoints: playerSession.Checkpoint[];
      }
    >();

    for (const cp of checkpoints) {
      const { group } = splitCheckpointKey(String(cp.name ?? ''));
      if (!group) continue;
      const existing = map.get(group);
      if (existing) existing.checkpoints.push(cp);
      else {
        map.set(group, {
          key: group,
          label: formatProgressCheckpointTitleOverview(group),
          icon: iconForCheckpoint(activeBadgeName, group),
          checkpoints: [cp],
        });
      }
    }

    const uniqueGroups = Array.from(map.keys());
    const shouldSkipGroupLayer = activeBadgeName === BadgeName.Putting || uniqueGroups.length <= 1;

    const makeStepItems = (grouped: playerSession.Checkpoint[]): CheckpointItem[] => {
      const sortedSteps = sortProgressCheckpoints(
        grouped.map(cp => ({
          name: String(cp.name),
          status: String(cp.status),
          attempts: cp.attempts,
        })) as any,
      ) as Array<{ name: string }>;
      const byName = new Map(grouped.map(cp => [String(cp.name), cp]));

      return sortedSteps
        .map(s => byName.get(String(s.name)))
        .filter((cp): cp is NonNullable<typeof cp> => Boolean(cp))
        .map(cp => {
          const key = splitCheckpointKey(String(cp.name));
          const label = key.hasDelimiter
            ? getStepDisplayLabelFromSuffix(key.suffix)
            : formatProgressCheckpointTitleOverview(String(cp.name));
          return {
            key: String(cp.name),
            label,
            icon: iconForCheckpoint(activeBadgeName, String(cp.name)),
            data: {
              name: activeBadgeName,
              badgeEarned: String(cp.status) === 'completed',
              dateEarned: activeBadgeData.dateEarned,
              checkpoints: [cp],
            } as playerSession.Badge,
            hideProgress: true,
          };
        });
    };

    if (shouldSkipGroupLayer) {
      const groupKey = uniqueGroups[0] ?? '';
      const grouped = groupKey ? (map.get(groupKey)?.checkpoints ?? []) : checkpoints;
      return makeStepItems(grouped);
    }

    if (activeGroup) {
      const grouped = map.get(activeGroup.key)?.checkpoints ?? [];
      return makeStepItems(grouped);
    }

    const sortedGroupKeys = sortNamesLikeProgress(uniqueGroups);
    return sortedGroupKeys
      .map(key => map.get(key))
      .filter((group): group is NonNullable<typeof group> => Boolean(group))
      .map(group => {
        const badgeEarned = group.checkpoints.every(c => String(c.status) === 'completed');
        return {
          key: group.key,
          label: group.label,
          icon: group.icon,
          data: {
            name: activeBadgeName,
            badgeEarned,
            dateEarned: activeBadgeData.dateEarned,
            checkpoints: group.checkpoints,
          } as playerSession.Badge,
          onPress: () => onSelectGroup({ key: group.key, label: group.label }),
          hideProgress: false,
        };
      });
  }

  // Flat list (no ".forms/.skill1/.skill2" prefixes): keep progress, except Scoring/Knowledge.
  const sorted = sortProgressCheckpoints(
    checkpoints.map(cp => ({
      name: String(cp.name),
      status: String(cp.status),
      attempts: cp.attempts,
    })) as any,
  ) as Array<{ name: string }>;

  const byName = new Map(checkpoints.map(cp => [String(cp.name), cp]));
  return sorted
    .map(s => byName.get(String(s.name)))
    .filter((cp): cp is NonNullable<typeof cp> => Boolean(cp))
    .map(cp => ({
      key: String(cp.name),
      label: formatProgressCheckpointTitleOverview(String(cp.name)),
      icon: iconForCheckpoint(activeBadgeName, String(cp.name)),
      data: {
        name: activeBadgeName,
        badgeEarned: String(cp.status) === 'completed',
        dateEarned: activeBadgeData.dateEarned,
        checkpoints: [cp],
      } as playerSession.Badge,
      hideProgress: alwaysHideProgress || getStepTokenFromName(String(cp.name)) !== null,
    }));
}

export const stateList: { icon: AvailableIconName; label: string; name: BadgeName }[] = [
  { icon: 'IcPutting', label: 'Putting', name: BadgeName.Putting },
  { icon: 'IcAroundthegreen', label: 'ATG', name: BadgeName.Aroundthegreen },
  { icon: 'IcFullSwing', label: 'Full Swing', name: BadgeName.Fullswing },
  { icon: 'IcMastery', label: 'Scoring', name: BadgeName.Mastery },
  { icon: 'IcKnowledge', label: 'Knowledge', name: BadgeName.Knowledge },
];

export function isSkillAchieved(badge: playerSession.Badge | undefined | null): boolean {
  if (!badge) return false;
  const cps = Array.isArray(badge.checkpoints) ? badge.checkpoints : [];
  if (cps.length === 0) return Boolean(badge.badgeEarned);
  return cps.every(cp => String(cp.status) === 'completed');
}

export function findNextIncompleteSkill(params: {
  progress: playerSession.Progress | undefined;
  fromBadge: BadgeName;
}): BadgeRef | null {
  const { progress, fromBadge } = params;
  if (!progress) return null;

  const startIdx = Math.max(
    0,
    stateList.findIndex(s => s.name === fromBadge),
  );
  const ordered = [...stateList.slice(startIdx + 1), ...stateList.slice(0, startIdx + 1)];

  for (const s of ordered) {
    const badge = progress.badges?.find(b => b.name === s.name);
    if (!isSkillAchieved(badge)) return { name: s.name, label: s.label };
  }

  return null;
}

export function getSkillIconName(badgeName: BadgeName): AvailableIconName {
  return stateList.find(s => s.name === badgeName)?.icon ?? 'IcScore';
}
